/**
 * School Collection Finder Web Component
 * Hybrid Architecture:
 * 1. Instant client-side search and alphabetical filter for /collections (0ms).
 * 2. Algolia Search and Predictive Search cross-sell for promoted gear and accessories.
 * Scoped Vanilla ES6+ Custom Element compatible with Shopify Section Rendering.
 */
if (!customElements.get('school-collection-finder')) {
  customElements.define('school-collection-finder', class extends HTMLElement {
    constructor() {
      super();
      this.input = null;
      this.clearBtn = null;
      this.letterContainer = null;
      this.countEl = null;
      this.emptyEl = null;
      this.resetBtn = null;
      this.promoContainer = null;
      this.promoGrid = null;
      this.cards = [];
      this.selectedLetter = 'all';
      this.searchQuery = '';
      this._debounceTimer = null;
      this._promoTimer = null;
      this._abortController = null;

      this._onInput = this._onInput.bind(this);
      this._onLetterClick = this._onLetterClick.bind(this);
      this._onClear = this._onClear.bind(this);
    }

    connectedCallback() {
      this.input = this.querySelector('.school-finder__input');
      this.clearBtn = this.querySelector('.school-finder__clear');
      this.letterContainer = this.querySelector('.school-finder__letters');
      this.countEl = this.querySelector('.school-finder__count');
      this.emptyEl = this.querySelector('.school-finder__empty');
      this.promoLimit = parseInt(this.getAttribute('data-promo-limit'), 10) || 8;
      this.resetBtn = this.querySelector('[data-action="clear-finder"]');
      this.promoContainer = this.querySelector('.school-finder__promotions');
      this.promoGrid = this.querySelector('[data-promo-grid]');
      this.cards = Array.from(this.querySelectorAll('[data-school-card]'));

      if (this.input) {
        this.input.addEventListener('input', this._onInput);
        this.input.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            this._onClear();
          }
        });
      }

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', this._onClear);
      }

      if (this.resetBtn) {
        this.resetBtn.addEventListener('click', this._onClear);
      }

      if (this.letterContainer) {
        this.letterContainer.addEventListener('click', this._onLetterClick);
      }

      this._renderLetters();
      this.updateFilter();
    }

    disconnectedCallback() {
      if (this._debounceTimer) {
        clearTimeout(this._debounceTimer);
      }
      if (this._promoTimer) {
        clearTimeout(this._promoTimer);
      }
      if (this._abortController) {
        this._abortController.abort();
      }
      if (this.input) {
        this.input.removeEventListener('input', this._onInput);
      }
      if (this.clearBtn) {
        this.clearBtn.removeEventListener('click', this._onClear);
      }
      if (this.resetBtn) {
        this.resetBtn.removeEventListener('click', this._onClear);
      }
      if (this.letterContainer) {
        this.letterContainer.removeEventListener('click', this._onLetterClick);
      }
    }

    _renderLetters() {
      if (!this.letterContainer) return;

      const lettersSet = new Set();
      this.cards.forEach((card) => {
        const letter = (card.getAttribute('data-letter') || '').toUpperCase();
        if (letter && /^[A-Z]$/.test(letter)) {
          lettersSet.add(letter);
        }
      });

      const sortedLetters = Array.from(lettersSet).sort();
      let html = '<button type="button" class="school-finder__letter is-active" data-letter="all" aria-pressed="true">All</button>';

      sortedLetters.forEach((l) => {
        html += `<button type="button" class="school-finder__letter" data-letter="${l}" aria-pressed="false">${l}</button>`;
      });

      this.letterContainer.innerHTML = html;
    }

    _onInput(e) {
      const val = e.target.value || '';
      clearTimeout(this._debounceTimer);
      clearTimeout(this._promoTimer);

      this._debounceTimer = setTimeout(() => {
        this.searchQuery = val.trim().toLowerCase();
        if (this.clearBtn) {
          this.clearBtn.style.display = this.searchQuery ? 'inline-flex' : 'none';
        }
        this.updateFilter();
      }, 50);

      this._promoTimer = setTimeout(() => {
        const query = val.trim();
        if (query.length >= 2) {
          this._fetchPromotions(query);
        } else {
          this._clearPromotions();
        }
      }, 220);
    }

    _onLetterClick(e) {
      const btn = e.target.closest('.school-finder__letter');
      if (!btn) return;

      const letter = btn.getAttribute('data-letter') || 'all';
      this.selectedLetter = letter;

      const allBtns = this.letterContainer.querySelectorAll('.school-finder__letter');
      allBtns.forEach((b) => {
        const isCurrent = b === btn;
        b.classList.toggle('is-active', isCurrent);
        b.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
      });

      this.updateFilter();
    }

    _onClear() {
      if (this.input) {
        this.input.value = '';
        this.input.focus();
      }
      this.searchQuery = '';
      this.selectedLetter = 'all';

      if (this.clearBtn) {
        this.clearBtn.style.display = 'none';
      }

      if (this.letterContainer) {
        const allBtns = this.letterContainer.querySelectorAll('.school-finder__letter');
        allBtns.forEach((b) => {
          const isAll = b.getAttribute('data-letter') === 'all';
          b.classList.toggle('is-active', isAll);
          b.setAttribute('aria-pressed', isAll ? 'true' : 'false');
        });
      }

      this._clearPromotions();
      this.updateFilter();
    }

    updateFilter() {
      let visibleCount = 0;
      const totalCount = this.cards.length;
      const q = this.searchQuery;
      const letter = this.selectedLetter;
      const cleanQ = q.replace(/[^a-z0-9]/g, '');

      this.cards.forEach((card) => {
        const title = (card.getAttribute('data-title') || '').toLowerCase();
        const handle = (card.getAttribute('data-handle') || '').toLowerCase();
        const acronym = (card.getAttribute('data-acronym') || '').toLowerCase();
        const firstLetter = (card.getAttribute('data-letter') || '').toUpperCase();

        const matchesLetter = letter === 'all' || firstLetter === letter;
        let matchesQuery = true;

        if (q) {
          const cleanTitle = title.replace(/[^a-z0-9]/g, '');
          matchesQuery = title.includes(q) ||
            handle.includes(q) ||
            acronym.includes(cleanQ) ||
            cleanTitle.includes(cleanQ);
        }

        const isVisible = matchesLetter && matchesQuery;
        card.style.display = isVisible ? '' : 'none';
        if (isVisible) {
          visibleCount++;
        }
      });

      if (this.countEl) {
        if (q || letter !== 'all') {
          this.countEl.textContent = `Showing ${visibleCount} of ${totalCount} schools`;
        } else {
          this.countEl.textContent = `Showing all ${totalCount} schools`;
        }
      }

      if (this.emptyEl) {
        this.emptyEl.style.display = visibleCount === 0 ? 'block' : 'none';
      }
    }

    async _fetchPromotions(query) {
      if (!this.promoContainer || !this.promoGrid) return;

      if (this._abortController) {
        this._abortController.abort();
      }
      this._abortController = new AbortController();

      try {
        let items = await this._searchAlgolia(query);
        if (!items || items.length === 0) {
          items = await this._searchShopifyPredictive(query, this._abortController.signal);
        }

        if (items && items.length > 0) {
          this._renderPromotions(items, query);
        } else {
          this._clearPromotions();
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          this._clearPromotions();
        }
      }
    }

    async _searchAlgolia(query) {
      if (!window.algoliaShopify) return null;

      try {
        const client = window.algoliaShopify.searchClient;
        const config = window.algoliaShopify.config || {};
        const indexPrefix = config.index_prefix || '';
        const indexName = `${indexPrefix}products`;

        if (client && typeof client.search === 'function') {
          const res = await client.search([{
            indexName: indexName,
            query: query,
            params: {
              hitsPerPage: this.promoLimit,
              clickAnalytics: true
            }
          }]);
          const hits = res?.results?.[0]?.hits;
          if (Array.isArray(hits) && hits.length > 0) {
            return hits.map((hit) => {
              const rawPrice = hit.price_min != null ? hit.price_min : hit.price;
              const priceCents = typeof rawPrice === 'number' && rawPrice > 1000 ? rawPrice : Math.round(Number(rawPrice || 0) * 100);
              return {
                id: hit.id || hit.objectID,
                variantId: hit.variants?.[0]?.id || hit.objectID,
                title: hit.title || hit.name || '',
                url: hit.url || `/products/${hit.handle}`,
                image: hit.product_image || hit.image || hit.featured_image || '',
                priceCents: priceCents,
                priceFormatted: this._formatMoney(priceCents),
                available: hit.inventory_quantity > 0 || hit.available !== false,
                vendor: hit.vendor || 'Uniform Gear'
              };
            });
          }
        }
      } catch (err) {
        // Fall back to Predictive Search
      }
      return null;
    }

    async _searchShopifyPredictive(query, signal) {
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const url = `${root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${this.promoLimit}&resources[options][unavailable_products]=hide`;
        const res = await fetch(url, {
          signal: signal,
          headers: { 'Accept': 'application/json' }
        });
        if (!res.ok) return [];
        const data = await res.json();
        const products = data?.resources?.results?.products || [];
        return products.map((prod) => {
          const priceCents = Math.round(Number(prod.price || 0) * 100);
          return {
            id: prod.id,
            variantId: prod.variants?.[0]?.id || '',
            title: prod.title,
            url: prod.url,
            image: prod.featured_image?.url || prod.image || '',
            priceCents: priceCents,
            priceFormatted: prod.price ? `$${Number(prod.price).toFixed(2)}` : this._formatMoney(priceCents),
            available: prod.available !== false,
            vendor: prod.vendor || 'Uniform Gear'
          };
        });
      } catch (err) {
        return [];
      }
    }

    _formatMoney(cents) {
      if (window.theme && typeof window.theme.formatMoney === 'function' && window.theme.moneyFormat) {
        return window.theme.formatMoney(cents, window.theme.moneyFormat);
      }
      return `$${(cents / 100).toFixed(2)}`;
    }

    _renderPromotions(items, query) {
      if (!this.promoContainer || !this.promoGrid) return;

      let html = '';
      items.forEach((item) => {
        const safeTitle = this._escapeHtml(item.title);
        const safeUrl = this._escapeHtml(item.url);
        const safeImg = this._escapeHtml(item.image);
        const safeVendor = this._escapeHtml(item.vendor);

        html += `
          <div class="school-finder__promo-card"
            data-product-id="${item.id}"
            data-variant-id="${item.variantId}"
            data-price-cents="${item.priceCents}"
            data-available="${item.available}">
            <a href="${safeUrl}" class="school-finder__promo-media" aria-label="${safeTitle}">
              ${safeImg ? `<img src="${safeImg}" alt="${safeTitle}" class="school-finder__promo-img" loading="lazy" width="240" height="240">` : ''}
            </a>
            <div class="school-finder__promo-body">
              <span class="school-finder__promo-vendor">${safeVendor}</span>
              <a href="${safeUrl}" class="school-finder__promo-item-title link">${safeTitle}</a>
              <div class="school-finder__promo-price">${item.priceFormatted}</div>
              <a href="${safeUrl}" class="school-finder__promo-btn">View Product</a>
            </div>
          </div>
        `;
      });

      this.promoGrid.innerHTML = html;
      this.promoContainer.style.display = 'block';
    }

    _clearPromotions() {
      if (this.promoContainer) {
        this.promoContainer.style.display = 'none';
      }
      if (this.promoGrid) {
        this.promoGrid.innerHTML = '';
      }
    }

    _escapeHtml(str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  });
}
